import {
  deletePrivateValue,
  getPrivateValue,
  pruneExpiredPrivateValues,
  setPrivateValue,
} from "./privateStorage";

const PARTNER_INVITE_PREFIX = "feel-scene:partner-invite:";
const PARTNER_INVITE_RECORD_PREFIX = "partner-invite:";

interface StoredPartnerInvite {
  url: string;
  expiresAt: number;
}

function removeLegacyInvite(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage may be unavailable under a browser privacy policy.
  }
}

export async function savePartnerInvite(
  roomId: string,
  url: string,
  expiresAt: number,
) {
  const key = `${PARTNER_INVITE_PREFIX}${roomId}`;
  try {
    await setPrivateValue(
      `${PARTNER_INVITE_RECORD_PREFIX}${roomId}`,
      { url, expiresAt } satisfies StoredPartnerInvite,
      expiresAt,
    );
    removeLegacyInvite(key);
    return;
  } catch {
    // Preserve recovery in browsers that block IndexedDB or Web Crypto storage.
  }
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ url, expiresAt } satisfies StoredPartnerInvite),
    );
  } catch {
    // The open room still works in memory when all persistence is blocked.
  }
}

export async function loadPartnerInvite(roomId: string) {
  const key = `${PARTNER_INVITE_PREFIX}${roomId}`;
  const recordId = `${PARTNER_INVITE_RECORD_PREFIX}${roomId}`;
  try {
    const stored = await getPrivateValue<Partial<StoredPartnerInvite>>(recordId);
    if (stored) {
      if (
        typeof stored.url === "string" &&
        typeof stored.expiresAt === "number" &&
        stored.expiresAt > Date.now()
      ) {
        removeLegacyInvite(key);
        return stored.url;
      }
      await deletePrivateValue(recordId);
    }
  } catch {
    // A legacy record can still be recovered and migrated below.
  }

  let legacy: Partial<StoredPartnerInvite>;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    legacy = JSON.parse(raw) as Partial<StoredPartnerInvite>;
  } catch {
    removeLegacyInvite(key);
    return null;
  }
  if (
    typeof legacy.url !== "string" ||
    typeof legacy.expiresAt !== "number" ||
    legacy.expiresAt <= Date.now()
  ) {
    removeLegacyInvite(key);
    return null;
  }
  try {
    await setPrivateValue(recordId, legacy, legacy.expiresAt);
    removeLegacyInvite(key);
  } catch {
    // Keep a valid legacy invite if encrypted persistence is unavailable.
  }
  return legacy.url;
}

export async function pruneExpiredPartnerInvites() {
  try {
    await pruneExpiredPrivateValues(PARTNER_INVITE_RECORD_PREFIX);
  } catch {
    // Cleanup must not block entry when private storage is unavailable.
  }

  try {
    const roomIds: string[] = [];
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(PARTNER_INVITE_PREFIX)) continue;
      roomIds.push(key.slice(PARTNER_INVITE_PREFIX.length));
    }
    await Promise.all(roomIds.map((roomId) => loadPartnerInvite(roomId)));
  } catch {
    // Storage cleanup must not block entry to the app.
  }
}

export async function clearRoomBrowserData(roomId: string) {
  try {
    localStorage.removeItem(`${PARTNER_INVITE_PREFIX}${roomId}`);
    localStorage.removeItem(`feel-scene:position:${roomId}:A`);
    localStorage.removeItem(`feel-scene:position:${roomId}:B`);
    localStorage.removeItem(`feel-scene:categories:${roomId}`);
  } catch {
    // Continue with in-memory cleanup when storage is unavailable.
  }

  try {
    const draftPrefix = `feel-scene:draft:${roomId}:`;
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(draftPrefix)) sessionStorage.removeItem(key);
    }
  } catch {
    // Continue with room deletion when storage is unavailable.
  }

  try {
    await deletePrivateValue(`${PARTNER_INVITE_RECORD_PREFIX}${roomId}`);
  } catch {
    // Continue after immediate browser cleanup when IndexedDB is unavailable.
  }
}
